/**
 * IntakePage - Partner A intake form
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Pre-fill from onboarding data if available
    const onboardingName = sessionStorage.getItem('onboarding_first_name') || '';
    const onboardingEmail = sessionStorage.getItem('onboarding_email') || '';
    const onboardingPartnerEmail = sessionStorage.getItem('onboarding_partner_email') || '';
    return {
      email: onboardingEmail,
      first_name: onboardingName,
      last_name: '',
      preferred_name: '',
      pronouns: '',
      location_country: '',
      location_state: '',
      relationship_status: 'committed',
      partner_email: onboardingPartnerEmail,
    };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const level = sessionStorage.getItem('selected_level');

      const response = await axios.post(`${API}/relationships`, {
        level: level,
        user_data: formData
      });

      sessionStorage.setItem('user_id', response.data.user_id);
      sessionStorage.setItem('relationship_id', response.data.relationship_id);
      sessionStorage.setItem('partner_email', formData.partner_email);
      
      toast.success('Profile created successfully!');
      navigate('/invite');
    } catch (error) {
      console.error('Error creating relationship:', error);
      toast.error('Failed to create profile');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} />
      <div className="py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-900 mb-2" data-testid="intake-heading">Partner A Intake</h2>
          <p className="text-stone-600 mb-12">Tell us about yourself to begin your MarrsingClause® experience.</p>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                data-testid="email-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Preferred Name (Optional)</label>
                <input
                  type="text"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({...formData, preferred_name: e.target.value})}
                  className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Pronouns (Optional)</label>
                <input
                  type="text"
                  value={formData.pronouns}
                  onChange={(e) => setFormData({...formData, pronouns: e.target.value})}
                  className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                  placeholder="e.g., she/her, he/him, they/them"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Relationship Status</label>
              <select
                value={formData.relationship_status}
                onChange={(e) => setFormData({...formData, relationship_status: e.target.value})}
                className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
              >
                <option value="married">Married</option>
                <option value="committed">Committed</option>
                <option value="engaged">Engaged</option>
                <option value="dating">Dating</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Partner Email *</label>
              <input
                type="email"
                required
                value={formData.partner_email}
                onChange={(e) => setFormData({...formData, partner_email: e.target.value})}
                className="w-full h-14 rounded-lg border-stone-200 bg-stone-50 px-4 text-lg focus:border-stone-900 focus:ring-0 transition-all"
                placeholder="Your partner will receive an invitation"
                data-testid="partner-email-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-intake"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IntakePage;
